import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { get, filter, map } from "lodash";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { CheckDuplicate } from "components/CheckDuplicate";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { ProgressSpinner } from "primereact/progressspinner";
import { Title } from "components/Title";
import useApp from "hooks/useApp";
import { useTranslation } from "react-i18next";
import useAddPage from "hooks/useAddPage";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
const UsersAddPage = (props) => {
  const app = useApp();
  const { t } = useTranslation();
  const USERS = t("USERS");
  const EMAIL = t("EMAIL");
  const SELECT_FACILITY_FIRST = t("SELECT_FACILITY_FIRST");
  const SELECT_MODULE = t("SELECT_MODULE");
  const FACILITIES = t("FACILITIES");
  const SELECT_FACILITY = t("SELECT_FACILITY");
  const NAME = t("NAME");
  const ROLE = t("ROLE");
  const ENTER_NAME = t("ENTER_NAME");
  const ACCOUNT_STATUS = t("ACCOUNT_STATUS");
  const ADD_NEW_USER = t("ADD_NEW_USER");
  const PASSWORD = t("PASSWORD");
  const CONFIRM_PASSWORD = t("CONFIRM_PASSWORD");
  const EMAIL_VERIFIED_AT = t("EMAIL_VERIFIED_AT");
  const ENTER_USER = t("ENTER_USER");
  const ENTER_EMAIL = t("ENTER_EMAIL");
  const ENTER_PASSWORD = t("ENTER_PASSWORD");
  const SELECT_ROLE = t("SELECT_ROLE");
  const MODULES = t("MODULES");
  const SUBMIT = t("SUBMIT");
  const RESET = t("RESET");
  const NOT_AVAILABLE = t("NOT_AVAILABLE");
  const CHECKING = t("CHECKING");
  const ENTER_ACCOUNT_STATUS = t("ENTER_ACCOUNT_STATUS");
  const [groupedModules, setGroupedModules] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState([]);
  //form validation rules
  const validationSchema = yup.object().shape({
    user: yup.string().required(t("USER_REQUIRED")),
    name: yup.string().required(t("NAME_REQUIRED")),
    email: yup.string().email().required(t("EMAIL_REQUIRED")),
    password: yup.string().required(t("PASSWORD_REQUIRED")),
    confirm_password: yup
      .string()
      .required(t("CONFIRM_PASSWORD_REQUIRED"))
      .oneOf([yup.ref("password")], t("YOUR_PASSWORD_DO_NOT_MATCH")),
    otp_code: yup.string().nullable().label("Otp Code"),
    otp_date: yup.string().nullable().label("Otp Date"),
    account_status: yup.string().required(t("ACCOUNT_STATUS_REQUIRED")),
    role_id: yup.string().required(t("ROLE_REQUIRED")),
    email_verified_at: yup.string().nullable().label("Email Verified At"),
  });

  //form default values
  const formDefaultValues = {
    user: "",
    email: "",
    name: "",
    password: "",
    confirm_password: "",
    otp_code: "",
    otp_date: new Date(),
    role_id: "",
    account_status: "",
    email_verified_at: new Date(),
  };

  //page hook where logics resides
  const pageController = useAddPage({ props, formDefaultValues, afterSubmit });

  // destructure and grab what the page needs
  const {
    formData,
    resetForm,
    handleSubmit,
    submitForm,
    pageReady,
    loading,
    saving,
    inputClassName,
  } = pageController;

  useEffect(() => {
    let filteredModules = props.modules;
    if (selectedOrg && selectedOrg.length > 0) {
      // Filter modules based on selected organizations
      filteredModules = filter(props.modules, (module) =>
        selectedOrg.includes(module.org_code)
      );
    }

    const selectedOrgs = filter(props.organisations, (org) =>
      selectedOrg.includes(org.code)
    );
    const groupedArray = map(selectedOrgs, (org) => {
      const orgModules = filter(
        filteredModules,
        (module) => module.org_code === org.code
      );
      return {
        label: org.name,
        code: org.code,
        items: map(orgModules, (module) => ({
          label: module.module_name,
          id: module.id,
        })),
      };
    });
    setGroupedModules(groupedArray);
  }, [selectedOrg, props.modules, props.organisations]);

  //event raised after form submit
  function afterSubmit(response) {
    app.flashMsg(props.msgTitle, props.msgAfterSave);
    resetForm();
    if (app.isDialogOpen()) {
      app.closeDialogs(); // if page is open as dialog, close dialog
    } else if (props.redirect) {
      app.navigate(`/users`);
    }
  }
  const handleReset = (formik) => {
    formik.resetForm();
  };
  const getInputClassName = (error, touched) => {
    return error && touched ? "p-invalid" : "";
  };
  const handleOrgChange = (e, formik) => {
    const selectedOrgs = e.value;
    formik.setFieldValue("orgs", selectedOrgs);
    setSelectedOrg(selectedOrgs);

    if (selectedOrgs.length === 0) {
      formik.setFieldValue("modules_ids", []);
    } else {
      const filteredModules = filter(app.modules, (module) =>
        selectedOrgs.includes(module.org_code)
      );
      const filteredModuleIds = map(filteredModules, "id");
      const newModuleIds = formik.values.modules_ids.filter((id) =>
        filteredModuleIds.includes(id)
      );
      formik.setFieldValue("modules_ids", newModuleIds);
    }
  };
  const toTitleCase = (str) => {
    return str.toLowerCase().replace(/(?:^|\s)\w/g, function (match) {
      return match.toUpperCase();
    });
  };
  const handleFieldChange = (name, value, formik) => {
    if (name === "email") {
      value = value.toLowerCase();
    }
    formik.handleChange({
      target: {
        name,
        value,
      },
    });
  };
  // page loading form data from api
  if (loading) {
    return (
      <div className="p-3 text-center">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }

  const groupedItemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <div style={{ fontWeight: "bolder" }}>
          FacilityName : {option.label}
        </div>
      </div>
    );
  };

  //page has loaded any required data and ready to render
  if (pageReady) {
    return (
      <main id="UsersAddPage" className="main-page">
        {props.showHeader && (
          <section className="page-section mb-3">
            <div className="container">
              <div className="grid align-items-center">
                {!props.isSubPage && (
                  <div className="col-fixed ">
                    <Button
                      onClick={() => app.navigate(-1)}
                      label=""
                      className="p-button p-button-text "
                      icon="pi pi-arrow-left"
                    />
                  </div>
                )}
                <div className="col ">
                  <Title
                    title={ADD_NEW_USER}
                    titleClass="text-2xl text-primary font-bold"
                    subTitleClass="text-500"
                    separator={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="md:col-9 sm:col-12 comp-grid">
                <div>
                  <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => submitForm(values)}
                  >
                    {(formik) => (
                      <>
                        <Form className={`${!props.isSubPage ? "card  " : ""}`}>
                          <div className="grid">
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{USERS} *</div>
                                <div className="col-12 md:col-9">
                                  <CheckDuplicate
                                    value={formik.values.user}
                                    apiPath="components_data/users_user_exist"
                                  >
                                    {(checker) => (
                                      <>
                                        <InputText
                                          name="user"
                                          onBlur={checker.check}
                                          onChange={(e) => {
                                            const titleCaseValue = toTitleCase(
                                              e.target.value
                                            );
                                            formik.handleChange({
                                              target: {
                                                name: "user",
                                                value: titleCaseValue,
                                              },
                                            });
                                          }}
                                          value={formik.values.user}
                                          label="User"
                                          type="text"
                                          placeholder={ENTER_USER}
                                          className={`w-full ${getInputClassName(
                                            formik.errors.user,
                                            formik.touched.user
                                          )}`}
                                          {...(formik.values.user && {
                                            showClear: true,
                                          })}
                                        />
                                        <ErrorMessage
                                          name="user"
                                          component="span"
                                          className="p-error"
                                        />
                                        {!checker.loading && checker.exist && (
                                          <small className="p-error">
                                            {NOT_AVAILABLE}
                                          </small>
                                        )}
                                        {checker.loading && (
                                          <small className="text-500">
                                            {CHECKING}
                                          </small>
                                        )}
                                      </>
                                    )}
                                  </CheckDuplicate>
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{EMAIL} *</div>
                                <div className="col-12 md:col-9">
                                  <CheckDuplicate
                                    value={formik.values.email}
                                    apiPath="components_data/users_email_exist"
                                  >
                                    {(checker) => (
                                      <>
                                        <InputText
                                          name="email"
                                          onBlur={checker.check}
                                          onChange={(e) =>
                                            handleFieldChange(
                                              "email",
                                              e.target.value,
                                              formik
                                            )
                                          }
                                          value={formik.values.email}
                                          label="Email"
                                          type="email"
                                          placeholder={ENTER_EMAIL}
                                          className={getInputClassName(
                                            formik?.errors?.email,
                                            formik.touched.email
                                          )}
                                          style={{ width: "100%" }}
                                        />
                                        <ErrorMessage
                                          name="email"
                                          component="span"
                                          className="p-error"
                                        />
                                        {!checker.loading && checker.exist && (
                                          <small className="p-error">
                                            {NOT_AVAILABLE}
                                          </small>
                                        )}
                                        {checker.loading && (
                                          <small className="text-500">
                                            {CHECKING}
                                          </small>
                                        )}
                                      </>
                                    )}
                                  </CheckDuplicate>
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{NAME} *</div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    type="text"
                                    name="name"
                                    placeholder={ENTER_NAME}
                                    onChange={formik.handleChange}
                                    value={formik.values.name}
                                    className={`w-full ${getInputClassName(
                                      formik.errors.name,
                                      formik.touched.name
                                    )}`}
                                    {...(formik.values.name && {
                                      showClear: true,
                                    })}
                                    filter
                                  />
                                  <ErrorMessage
                                    name="name"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {PASSWORD} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Password
                                    name="password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    label="Password"
                                    placeholder={ENTER_PASSWORD}
                                    inputClassName="w-full"
                                    toggleMask
                                    feedback
                                    className={getInputClassName(
                                      formik?.errors?.password,
                                      formik.touched.password
                                    )}
                                    style={{ width: "100%" }}
                                  />
                                  <ErrorMessage
                                    name="password"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {CONFIRM_PASSWORD} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Password
                                    name="confirm_password"
                                    id="confirm_password"
                                    className={`w-full ${getInputClassName(
                                      formik.errors.confirm_password,
                                      formik.touched.confirm_password
                                    )}`}
                                    inputClassName="w-full"
                                    feedback={false}
                                    toggleMask
                                    value={formik.values.confirm_password}
                                    onChange={formik.handleChange}
                                    label="Confirm Password"
                                    placeholder={CONFIRM_PASSWORD}
                                  />
                                  <ErrorMessage
                                    name="confirm_password"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {FACILITIES}
                                </div>
                                <div className="col-12 md:col-9">
                                  <MultiSelect
                                    name="orgs"
                                    emptyFilterMessage={t("NO_OPTIONS_FOUND")}
                                    value={formik.values.orgs}
                                    onChange={(e) => handleOrgChange(e, formik)}
                                    options={get(props, "organisations", [])}
                                    optionLabel="name"
                                    optionValue="code"
                                    placeholder={SELECT_FACILITY}
                                    className={inputClassName(
                                      formik?.errors?.orgs
                                    )}
                                    display="chip"
                                    filter
                                    maxSelectedLabels={4}
                                  />
                                  <ErrorMessage
                                    name="orgs"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{MODULES}</div>
                                <div className="col-12 md:col-9">
                                  <MultiSelect
                                    name="modules_ids"
                                    value={formik.values.modules_ids}
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "modules_ids",
                                        e.value
                                      );
                                    }}
                                    options={groupedModules}
                                    optionLabel="label"
                                    optionGroupLabel="label"
                                    optionGroupChildren="items"
                                    optionValue="id"
                                    emptyFilterMessage={t("NO_OPTIONS_FOUND")}
                                    optionGroupTemplate={groupedItemTemplate}
                                    placeholder={
                                      formik.values.orgs?.length > 0
                                        ? SELECT_MODULE
                                        : SELECT_FACILITY_FIRST
                                    }
                                    className={inputClassName(
                                      formik?.errors?.orgs
                                    )}
                                    display="chip"
                                    filter
                                  />
                                  <ErrorMessage
                                    name="modules_ids"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{ROLE} *</div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    value={formik.values.role_id}
                                    {...(formik.values.role_id && {
                                      showClear: true,
                                    })}
                                    onChange={(e) => {
                                      const newValue =
                                        e.value === undefined ? "" : e.value;
                                      formik.setFieldValue("role_id", newValue);
                                      formik.setFieldTouched("role-id", false);
                                    }}
                                    options={get(props, "roles", [])}
                                    optionLabel="name"
                                    optionValue="id"
                                    placeholder={SELECT_ROLE}
                                    appendTo="self"
                                    className={getInputClassName(
                                      formik?.errors?.role_id,
                                      formik.touched.role_id
                                    )}
                                    style={{ width: "100%" }}
                                  />
                                  <ErrorMessage
                                    name="role_id"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {ACCOUNT_STATUS} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    options={props.accountStatus.map(
                                      (status) => ({
                                        name: status,
                                        value: status,
                                      })
                                    )}
                                    optionLabel="name"
                                    placeholder={ENTER_ACCOUNT_STATUS}
                                    className={`w-full ${getInputClassName(
                                      formik.errors.account_status,
                                      formik.touched.account_status
                                    )}`}
                                    value={formik.values.account_status}
                                    onChange={(e) => {
                                      const newValue =
                                        e.value === undefined ? "" : e.value;
                                      formik.setFieldValue(
                                        "account_status",
                                        newValue,
                                        formik.setFieldTouched(
                                          "account_status",
                                          false
                                        )
                                      );
                                    }}
                                    {...(formik.values.account_status && {
                                      showClear: true,
                                    })}
                                  />
                                  <ErrorMessage
                                    name="account_status"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {EMAIL_VERIFIED_AT}
                                </div>
                                <div className="col-12 md:col-9">
                                  <Calendar
                                    name="email_verified_at"
                                    value={formik.values.email_verified_at}
                                    onChange={formik.handleChange}
                                    showButtonBar
                                    showTime
                                    dateFormat="yy-mm-dd"
                                    hourFormat="24"
                                    showIcon
                                    hideOnDateTimeSelect
                                    className={inputClassName(
                                      formik?.errors?.email_verified_at
                                    )}
                                  />
                                  <ErrorMessage
                                    name="email_verified_at"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {props.showFooter && (
                            <div className="text-center my-3">
                              <Button
                                onClick={(e) => handleSubmit(e, formik)}
                                className="mr-2"
                                type="submit"
                                label={SUBMIT}
                                icon="pi pi-send"
                                loading={saving}
                              />
                              <Button
                                onClick={() => handleReset(formik)}
                                className="p-button-secondary mr-2"
                                type="button"
                                label={RESET}
                                icon="pi pi-refresh"
                              />
                            </div>
                          )}
                        </Form>
                      </>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }
};

//page props and default values
const withDefaultProps = (Component) => {
  const WrappedComponent = (props) => {
    const { t } = useTranslation();
    const defaultProps = {
      primaryKey: "id",
      pageName: "users",
      apiPath: "users/add",
      routeName: "usersadd",
      submitButtonLabel: "Submit",
      formValidationError: t("FORM_IS_INVALID"),
      formValidationMsg: t("PLEASE_COMPLETE_THE_FORM"),
      msgTitle: t("CREATE_USER"),
      msgAfterSave: t("USER_CREATED_SUCCESSFULLY"),
      msgBeforeSave: "",
      showHeader: true,
      showFooter: true,
      redirect: true,
      isSubPage: false,
    };
    return <Component {...defaultProps} {...props} />;
  };

  return WrappedComponent;
};
export default withDefaultProps(UsersAddPage);
